import styled from '@emotion/styled'
import { concat, includes, without } from 'lodash'
import {
  breakpoints,
  Checkbox,
  Hidden,
  Popover,
  QuestionIcon,
  spacing,
  Strong,
  Text,
  Visible,
} from 'main/ui'
import { IFilters } from 'main/util/search'
import * as React from 'react'

import { LOCIZE_NAMESPACE as NAMESPACE } from 'main/common'
import { useWhitelabel } from 'main/framework'
import { useTranslation } from 'react-i18next'
import {
  Apply,
  CheckboxGroupContainer,
  Clear,
  FieldCopyContainer,
  FieldsContainer,
  FilterFormContainer as CommonFilterFormContainer,
  FilterFormSubmitContainer,
} from '../../common/FilterForm.styles'
import { ISearchFormProps } from '../../types'

const SpaceBetweenFlexSpan = styled.span`
  display: flex;
  flex-direction: column;
  padding-left: 0;

  @media (min-width: ${breakpoints.lg}px) {
    flex-direction: row;
    justify-content: space-between;
  }
`

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`

const FilterFormContainer = styled(CommonFilterFormContainer)`
  @media (min-width: ${breakpoints.lg}px) {
    padding: ${spacing.majorScale(3)}px ${spacing.majorScale(4)}px ${spacing.majorScale(4)}px;
    max-width: 340px;
  }
`

const TooltipIconContainer = styled.div`
  display: inline-block;
  cursor: pointer;
  height: 17px;
  margin-left: ${spacing.majorScale(1)}px;
  vertical-align: middle;
  width: 17px;
`

const TooltipContainer = styled.div`
  max-width: 300px;
  display: flex;
  flex-direction: column;

  > :not(:last-child) {
    margin-bottom: ${spacing.majorScale(1)}px;
  }
`

export default function FilterForm(props: ISearchFormProps) {
  const { filters, onChangeFilters, onRequestCloseForm } = props
  const [params, setParams] = React.useState<IFilters>({ ...filters })

  const { t: translate } = useTranslation(NAMESPACE)
  function c(suffix: string): string {
    return translate(`COMMON-${suffix}`)
  }

  const handleApply = React.useCallback(() => {
    onRequestCloseForm()
    onChangeFilters({ ...filters, selectedProgramOfferings: params.selectedProgramOfferings })
  }, [params, onRequestCloseForm, onChangeFilters])

  const handleClear = React.useCallback(() => {
    setParams({ ...params, selectedProgramOfferings: [] })
  }, [params])

  return (
    <FilterFormContainer>
      <Fields params={params} onChangeParams={setParams} />
      <Visible lg xl>
        <FilterFormSubmitContainer css={{ marginTop: spacing.minorScale(5) }}>
          <Clear onClick={() => handleClear()}>{c('CLEAR')}</Clear>
          <Apply onClick={() => handleApply()}>{c('APPLY')}</Apply>
        </FilterFormSubmitContainer>
      </Visible>
    </FilterFormContainer>
  )
}

function Tooltip({ slug }: { slug: string }) {
  const [isTooltipOpen, setIsTooltipOpen] = React.useState<boolean>(false)
  const { t: translate } = useTranslation(NAMESPACE)
  const tooltipText = translate(`FILTERBAR-PROGRAM-OFFERINGS-${slug.toUpperCase()}-TOOLTIP`)

  return (
    <Popover
      body={
        <TooltipContainer>
          <Text>{tooltipText}</Text>
        </TooltipContainer>
      }
      isOpen={isTooltipOpen}
      onRequestClose={() => setIsTooltipOpen(false)}
    >
      <TooltipIconContainer onClick={() => setIsTooltipOpen(true)}>
        <QuestionIcon />
      </TooltipIconContainer>
    </Popover>
  )
}

export function Fields(fieldsProps: {
  params: IFilters
  onChangeParams: (params: IFilters) => void
}) {
  const { params, onChangeParams } = fieldsProps
  const { networkProgramOfferings } = useWhitelabel()
  const { t: translate } = useTranslation(NAMESPACE)

  function c(lang: string): string {
    return translate(`PROGRAM-OFFERING-${lang.toUpperCase()}`)
  }

  function t(suffix: string): string {
    return translate(`FILTERBAR-PROGRAM-OFFERINGS-${suffix}`)
  }

  return (
    <div>
      <Visible lg xl>
        <SpaceBetweenFlexSpan>
          <Strong>{t('LABEL')}</Strong>
        </SpaceBetweenFlexSpan>
      </Visible>
      <Hidden lg xl>
        <FieldCopyContainer>
          <Text>
            <Strong>{t('LABEL')}</Strong>
          </Text>
        </FieldCopyContainer>
      </Hidden>
      <FieldsContainer>
        <CheckboxGroupContainer css={{ width: 'max-content' }}>
          {networkProgramOfferings.map(option => {
            return (
              <CheckboxContainer key={option.id}>
                <Checkbox
                  checked={includes(params.selectedProgramOfferings, option.id)}
                  label={c(option.slug)}
                  onChange={(checked: boolean) => {
                    checked
                      ? onChangeParams({
                          ...params,
                          selectedProgramOfferings: concat(
                            params.selectedProgramOfferings,
                            option.id,
                          ),
                        })
                      : onChangeParams({
                          ...params,
                          selectedProgramOfferings: without(
                            params.selectedProgramOfferings,
                            option.id,
                          ),
                        })
                  }}
                />
                <Tooltip slug={option.slug} />
              </CheckboxContainer>
            )
          })}
        </CheckboxGroupContainer>
      </FieldsContainer>
    </div>
  )
}
